import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, CircleUserRound, Footprints, Hand, Lightbulb, PersonStanding, Shirt, ZoomIn } from 'lucide-react'
import { IdleAnimation, SkinViewer, WalkingAnimation } from 'skinview3d'
import { api } from './api'
import { Button, Segmented, SelectInput } from './components'
import { dialogueTargetLabel } from './dialogueContract'
import { dialogueTrackPosition } from './dialoguePlayback'
import type { DialoguePackage, NpcBody } from './types'
import { equipmentPreview } from './equipmentGeometry'
import type { EquipmentAttachment } from './equipmentGeometry'
import './skin-preview.css'

type Pose = 'idle' | 'walk' | 'sit' | 'look'
type ViewerObject = SkinViewer['playerObject']['skin']['head']['innerLayer']
type ViewerGeometry = { clone(): ViewerGeometry; dispose(): void }
type ViewerColor = { setHex(value: number): void }
type ViewerMaterial = {
  clone(): ViewerMaterial
  dispose(): void
  color?: ViewerColor
  map?: unknown
  needsUpdate?: boolean
}
type RenderableViewerObject = ViewerObject & {
  geometry: ViewerGeometry
  material: ViewerMaterial | ViewerMaterial[]
}

export function SkinPreview({ projectId, body, skinUrl, dialoguePackageId }: { projectId: string; body: NpcBody; skinUrl?: string; dialoguePackageId?: string }) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const viewer = useRef<SkinViewer | null>(null)
  const equipmentMeshes = useRef<ViewerObject[]>([])
  const [pose, setPose] = useState<Pose>('idle')
  const [outerLayers, setOuterLayers] = useState(true)
  const [autoRotate, setAutoRotate] = useState(true)
  const [lighting, setLighting] = useState(1)
  const [zoom, setZoom] = useState(0.9)
  const [dialoguePlaying, setDialoguePlaying] = useState(false)
  const equipment = body.equipment
  const heldItem = body.heldItem
  const previewEquipment = useMemo(
    () => equipmentPreview({ equipment, heldItem }),
    [equipment, heldItem],
  )

  useEffect(() => {
    if (!canvas.current || !skinUrl) return
    const instance = new SkinViewer({
      canvas: canvas.current,
      width: 340,
      height: 440,
      background: 0x17191d,
      enableControls: true,
    })
    instance.nameTag = null
    instance.controls.enablePan = false
    instance.controls.enableRotate = true
    instance.controls.enableZoom = true
    viewer.current = instance
    return () => {
      viewer.current = null
      instance.dispose()
    }
  }, [skinUrl])

  useEffect(() => {
    if (!viewer.current || !skinUrl) return
    void viewer.current.loadSkin(skinUrl, { model: body.skinModel === 'classic' ? 'default' : 'slim' })
  }, [body.skinModel, skinUrl])

  useEffect(() => {
    const instance = viewer.current
    if (!instance) return
    instance.playerObject.scale.setScalar(body.bodyScale)
    instance.autoRotate = autoRotate
    instance.zoom = zoom
    instance.globalLight.intensity = lighting
    instance.cameraLight.intensity = 0.7 * lighting
    for (const part of ['head', 'body', 'leftArm', 'rightArm', 'leftLeg', 'rightLeg'] as const) {
      instance.playerObject.skin[part].outerLayer.visible = outerLayers
    }
    const renderedPose = dialoguePlaying ? 'look' : pose
    if (renderedPose === 'walk') {
      instance.animation = new WalkingAnimation()
      instance.animation.speed = 0.8
    } else {
      instance.animation = new IdleAnimation()
      instance.animation.speed = renderedPose === 'look' ? 0.45 : 0.25
      const skin = instance.playerObject.skin
      if (renderedPose === 'sit') {
        skin.leftLeg.rotation.x = -Math.PI / 2
        skin.rightLeg.rotation.x = -Math.PI / 2
        skin.leftArm.rotation.x = -0.25
        skin.rightArm.rotation.x = -0.25
      } else if (renderedPose === 'look') {
        skin.head.rotation.y = 0.38
        skin.head.rotation.x = -0.08
      }
    }
  }, [autoRotate, body.bodyScale, dialoguePlaying, lighting, outerLayers, pose, zoom])

  useEffect(() => {
    const instance = viewer.current
    if (!instance) return
    for (const mesh of equipmentMeshes.current) {
      disposeEquipmentMesh(mesh)
    }
    const template = firstRenderable(instance.playerObject.skin.head.innerLayer)
    const meshes = template
      ? previewEquipment.attachments.map((attachment) => {
        const mesh = cloneEquipmentMesh(template, attachment)
        instance.playerObject.skin[attachment.part].add(mesh)
        return mesh
      })
      : []
    equipmentMeshes.current = meshes
    return () => {
      for (const mesh of meshes) disposeEquipmentMesh(mesh)
      if (equipmentMeshes.current === meshes) equipmentMeshes.current = []
    }
  }, [previewEquipment, skinUrl])

  return (
    <section className="skin-preview" aria-label="Three-dimensional NPC preview">
      <div className="preview-stage">
        {!skinUrl && <div className="preview-empty"><CircleUserRound size={42} /><span>Assign a skin to render the exact NPC</span></div>}
        <canvas ref={canvas} aria-label={`${body.displayName || 'NPC'} skin preview`} />
        <div className="preview-badges"><span>{body.skinModel}</span><span>{body.bodyScale.toFixed(2)}×</span><span>age {body.age}</span></div>
        {body.heldItem && <div className="equipment-chip"><Hand size={14} /> {body.heldItem}</div>}
        {Object.values(body.equipment).filter(Boolean).length > 0 && <div className="equipment-chip second"><Shirt size={14} /> {Object.values(body.equipment).filter(Boolean).join(', ')}</div>}
        {previewEquipment.issues.length > 0 && <div className="equipment-preview-status" aria-label="Equipment preview limitations">{previewEquipment.issues.map((issue) => <span key={`${issue.slot}-${issue.item}`} className={`equipment-${issue.status}`}><strong>{issue.item}</strong> · {issue.message}</span>)}</div>}
      </div>
      <div className="preview-controls">
        <Segmented value={pose} label="Preview pose" options={[
          { value: 'idle', label: 'Idle' }, { value: 'walk', label: 'Walk' }, { value: 'sit', label: 'Sit' }, { value: 'look', label: 'Look' },
        ]} onChange={setPose} />
        <div className="preview-sliders">
          <label><ZoomIn size={15} /><span>Zoom</span><input type="range" min="0.6" max="1.35" step="0.05" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} /></label>
          <label><Lightbulb size={15} /><span>Light</span><input type="range" min="0.3" max="2" step="0.1" value={lighting} onChange={(event) => setLighting(Number(event.target.value))} /></label>
        </div>
        <div className="preview-toggles">
          <button type="button" className={outerLayers ? 'active' : ''} onClick={() => setOuterLayers((value) => !value)} aria-pressed={outerLayers}><Box size={15} /> Outer layers</button>
          <button type="button" className={autoRotate ? 'active' : ''} onClick={() => setAutoRotate((value) => !value)} aria-pressed={autoRotate}><PersonStanding size={15} /> Rotate</button>
          <span title="Mouse/touch drag rotates; wheel/pinch zooms"><Footprints size={15} /> Interactive</span>
        </div>
        {body.interaction.type === 'dialogue' && dialoguePackageId && <ImportedDialoguePreview projectId={projectId} packageId={dialoguePackageId} onPlayingChange={setDialoguePlaying}/>} 
      </div>
    </section>
  )
}

export function ImportedDialoguePreview({ projectId, packageId, onPlayingChange }: { projectId: string; packageId: string; onPlayingChange: (playing: boolean) => void }) {
  const audio = useRef<HTMLAudioElement>(null)
  const [dialogue, setDialogue] = useState<DialoguePackage>()
  const [lineId, setLineId] = useState('')
  const [playing, setPlaying] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    setDialogue(undefined)
    setLineId('')
    setError('')
    void api.dialogue(projectId, packageId).then(value => {
      if (!active) return
      setDialogue(value)
      const first = value.body.lines.find(line => line.audioId && value.body.audio.some(track => track.id === line.audioId))
      setLineId(first?.id ?? '')
    }).catch(reason => {
      if (active) setError(reason instanceof Error ? reason.message : 'Dialogue preview could not be loaded')
    })
    return () => { active = false }
  }, [packageId, projectId])

  useEffect(() => {
    onPlayingChange(playing)
    return () => onPlayingChange(false)
  }, [onPlayingChange, playing])

  useEffect(() => {
    audio.current?.pause()
    setPlaying(false)
  }, [lineId])

  if (error) return <div className="dialogue-preview-compact"><strong>Imported dialogue preview</strong><small className="field-error">{error}</small></div>
  if (!dialogue) return <div className="dialogue-preview-compact"><strong>Imported dialogue preview</strong><small>Loading synchronized audio…</small></div>

  const lines = dialogue.body.lines.filter(line => line.audioId && dialogue.body.audio.some(track => track.id === line.audioId))
  const line = lines.find(value => value.id === lineId) ?? lines[0]
  const track = line?.audioId ? dialogue.body.audio.find(value => value.id === line.audioId) : undefined
  const start = track && line ? dialogueTrackPosition(line.startMs, track, dialogue.durationMs) : undefined
  const end = track && line ? dialogueTrackPosition(line.endMs, track, dialogue.durationMs) : undefined
  const playable = Boolean(track && line && start?.state === 'active' && end?.localMs !== undefined && end.localMs > start.localMs)

  async function toggle() {
    const element = audio.current
    if (!element || !start || !playable) return
    if (playing) {
      element.pause()
      setPlaying(false)
      return
    }
    element.currentTime = start.localMs / 1000
    setError('')
    try {
      await element.play()
      setPlaying(true)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Browser blocked dialogue playback')
    }
  }

  return <div className="dialogue-preview-compact">
    <div><strong>Imported dialogue preview</strong><small>{dialogue.name} · source {dialogue.sourceVersion}</small></div>
    {lines.length ? <>
      <SelectInput aria-label="Dialogue preview line" value={line?.id ?? ''} onChange={event => setLineId(event.target.value)}>{lines.map(value => <option key={value.id} value={value.id}>{value.speaker ?? value.speakerRole ?? 'Speaker'} · {(value.startMs / 1000).toFixed(1)}s</option>)}</SelectInput>
      {track && <audio hidden ref={audio} src={api.dialogueAudioUrl(projectId, dialogue.id, track.id, dialogue.version)} preload="metadata" onEnded={() => setPlaying(false)} onTimeUpdate={event => { if (end && event.currentTarget.currentTime * 1000 >= end.localMs) { event.currentTarget.pause(); setPlaying(false) } }}/>} 
      <Button variant="quiet" disabled={!playable} onClick={() => void toggle()}>{playing ? 'Pause line' : 'Play line'}</Button>
      <p>{line?.text}</p>
      {line && <small>Target: {dialogueTargetLabel(line)} / movement: {line.movementBehavior}</small>}
    </> : <small>No line in this package owns a declared audio track.</small>}
  </div>
}

function isRenderable(value: ViewerObject): value is RenderableViewerObject {
  const candidate = value as ViewerObject & Partial<RenderableViewerObject>
  return candidate.geometry !== undefined && candidate.material !== undefined
}

function firstRenderable(root: ViewerObject): RenderableViewerObject | null {
  if (isRenderable(root)) return root
  for (const child of root.children) {
    const found = firstRenderable(child)
    if (found) return found
  }
  return null
}

function cloneEquipmentMesh(template: RenderableViewerObject,
                            attachment: EquipmentAttachment): ViewerObject {
  const mesh = template.clone()
  if (!isRenderable(mesh)) throw new Error('Skin renderer returned a non-renderable clone')
  mesh.geometry = template.geometry.clone()
  const materials = Array.isArray(template.material) ? template.material : [template.material]
  const clonedMaterials = materials.map((material) => {
    const clone = material.clone()
    clone.color?.setHex(attachment.color)
    if ('map' in clone) clone.map = null
    clone.needsUpdate = true
    return clone
  })
  mesh.material = Array.isArray(template.material) ? clonedMaterials : clonedMaterials[0]
  mesh.name = `extranpcs-equipment-${attachment.item}`
  mesh.scale.set(attachment.size[0] / 8, attachment.size[1] / 8,
    attachment.size[2] / 8)
  mesh.position.set(...attachment.position)
  mesh.rotation.set(...attachment.rotation)
  return mesh
}

function disposeEquipmentMesh(mesh: ViewerObject): void {
  mesh.removeFromParent()
  if (!isRenderable(mesh)) return
  mesh.geometry.dispose()
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  materials.forEach((material) => material.dispose())
}
